import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { DataTable } from "../../../components/DataTable.jsx";
import { useAuth } from "../../login/AuthContext";
import { useNavigate } from "react-router-dom";
import { AddCircleOutline } from "@mui/icons-material";
import axios from "axios";
import config from "../../../config/config.js";
import IconActionsRenderer from "../../../components/IconActionsRenderer.jsx";
import { dateFormatter } from "../../../components/DataTable/dateFormatter.js";
import { StatusCellRenderer } from "../../../components/DataTable/StatusCellRenderer.jsx";
import "../../customers/customers.css";

export default function GastoraOrders() {
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const searchTimeoutRef = useRef(null);
  const dataTableRef = useRef(null);
  const [aktifPasif, setAktifPasif] = useState("aktif");
  const isBayiFullAccess = Number(userData?.BayiId ?? userData?.BayiKodu ?? 0) === 1;
  const customQueryParams = useMemo(() => ({ aktifPasif }), [aktifPasif]);

  useEffect(() => {
    if (!dataTableRef.current?.refreshData) return;
    const t = setTimeout(() => dataTableRef.current.refreshData(), 0);
    return () => clearTimeout(t);
  }, [aktifPasif]);

  const handleDeleteOrder = useCallback(
    async (id) => {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
      return api.put(`/gastora-siparis/delete/${id}`, { params: { aktifPasif } });
    },
    [token, aktifPasif]
  );

  const handleForwardOrder = useCallback(
    async (orderId) => {
      const api = axios.create({
        baseURL: config.baseURL,
        headers: { Authorization: `Bearer ${token}` },
      });
      return api.put(`/gastora-siparis/${orderId}/yonlendir`);
    },
    [token]
  );

  const handleSearch = (value) => {
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (dataTableRef.current?.refreshData) {
        dataTableRef.current.refreshData();
      }
    }, 300);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "İşlemler",
        field: "actions",
        width: 130,
        minWidth: 130,
        maxWidth: 130,
        sortable: false,
        filter: false,
        cellRenderer: IconActionsRenderer,
        pinned: "left",
        cellClass: "action-cell-center",
      },
      {
        headerName: "Sipariş Tarihi",
        field: "Tarih",
        valueFormatter: dateFormatter,
        sortable: true,
        filter: "agDateColumnFilter",
        width: 120,
      },
      {
        headerName: "Sipariş No",
        field: "Id",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 110,
      },
      {
        headerName: "Bayi",
        field: "BayiAdi",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: "Ünvan",
        field: "Unvan",
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        headerName: "Lisans Tipi",
        field: "LisansTipi",
        sortable: true,
        filter: true,
        width: 120,
      },
      {
        headerName: "Durum",
        field: "DurumAd",
        sortable: true,
        filter: true,
        width: 140,
        cellRenderer: StatusCellRenderer,
      },
      {
        headerName: "Ürün",
        field: "UrunAilesi",
        sortable: true,
        filter: true,
        width: 110,
        valueFormatter: (p) => p.value || "Gastro",
      },
    ],
    []
  );

  return (
    <div className="cari-listesi-container">
      <div className="header">
        <h1>Gastro Siparişler</h1>
      </div>

      {isBayiFullAccess && (
        <FormControl component="fieldset" sx={{ mb: 1.5, display: "block" }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 0.5 }}>
            Liste
          </FormLabel>
          <RadioGroup
            row
            value={aktifPasif}
            onChange={(e) => setAktifPasif(e.target.value)}
            name="aktif-pasif-gastora-siparis"
          >
            <FormControlLabel value="aktif" control={<Radio size="small" />} label="Aktif" />
            <FormControlLabel value="pasif" control={<Radio size="small" />} label="Pasifler" />
          </RadioGroup>
        </FormControl>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Gelişmiş Arama"
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircleOutline />}
          onClick={() => navigate("/gastora/siparis")}
          sx={{ ml: 2 }}
          size="medium"
        >
          Ekle
        </Button>
      </div>

      <DataTable
        ref={dataTableRef}
        searchText={searchText}
        tableType="orders"
        columnDefs={columnDefs}
        apiEndpoint="/gastora-siparis"
        searchEndpoint="/gastora-siparis/search"
        editPath="/gastora/siparis"
        onRowDoubleClicked={(e) => {
          const id = e.data?.Id;
          if (id != null) navigate(`/gastora/siparis/${id}`);
        }}
        customQueryParams={customQueryParams}
        onForwardAction={handleForwardOrder}
        onDeleteAction={handleDeleteOrder}
        forwardConfirmMessage="Bu Gastro Siparişini üst bayiye yönlendirmek istediğinizden emin misiniz?"
        deleteConfirmMessage={
          aktifPasif === "pasif"
            ? "Bu siparişi kalıcı olarak silmek istediğinizden emin misiniz?"
            : "Bu siparişi silmek istediğinizden emin misiniz?"
        }
      />
    </div>
  );
}
